import {
  Badge,
  Button,
  Card,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  FileJson,
  Layers,
  Plus,
  Settings,
  Trash2,
} from 'lucide-react';
import {
  BuilderQuestionGroup,
  SectionSettingsDialog,
  SubsectionSettingsDialog,
  createNewSubsection,
  createNewGroup,
  useFormDefinition,
  useCurrentSectionIndex,
  useCurrentSubsectionIndex,
  useCanGoNext,
  useCanGoPrevious,
  getFormBuilderActions,
  formatConditionalBehaviorText,
} from '@/features/quote-and-apply-builder';
import { useFormEditorContent } from '@/features/quote-and-apply-builder/hooks/use-form-editor-content';

export function FormEditorContent() {
  const formDefinition = useFormDefinition();
  const currentSectionIndex = useCurrentSectionIndex();
  const currentSubsectionIndex = useCurrentSubsectionIndex();
  const canGoNext = useCanGoNext();
  const canGoPrevious = useCanGoPrevious();
  const {
    updateSection,
    removeSection,
    addSubsection,
    updateSubsection,
    removeSubsection,
    addGroup,
    navigateNext,
    navigatePrevious,
    navigateTo,
    duplicateSection,
    duplicateSubsection,
    exportForm,
    exportSection,
  } = getFormBuilderActions();

  const {
    showSectionSettings,
    showSubsectionSettings,
    openSectionSettings,
    closeSectionSettings,
    openSubsectionSettings,
    closeSubsectionSettings,
  } = useFormEditorContent();

  const section = formDefinition.sections[currentSectionIndex];

  if (!section) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-2 text-gray-500">No sections in this form.</p>
          <p className="text-sm text-gray-400">Add a section from the sidebar to get started.</p>
        </div>
      </div>
    );
  }

  const subsection = section.subsections[currentSubsectionIndex];

  return (
    <div className="bg-muted flex-1 overflow-y-auto pb-20">
      <div className="mx-auto max-w-4xl p-6 md:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-950">
            {section.title || 'Untitled Section'}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newSubsection = createNewSubsection();
                addSubsection(section.id, newSubsection);
                navigateTo(currentSectionIndex, section.subsections.length);
              }}
            >
              <Plus className="mr-1 size-4" />
              Add Subsection
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                >
                  <Download className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(exportForm, 0);
                  }}
                >
                  <FileJson className="mr-2 size-4" />
                  Export Full Form
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(() => exportSection(currentSectionIndex), 0);
                  }}
                >
                  <Layers className="mr-2 size-4" />
                  Export Current Section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {section.conditional && (
              <Tooltip>
                <TooltipTrigger className="cursor-default">
                  <Badge variant="success">Conditions</Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs p-2">
                  {formatConditionalBehaviorText(section.conditional, formDefinition.questions)}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => duplicateSection(currentSectionIndex)}
                >
                  <Copy className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Duplicate Section</TooltipContent>
            </Tooltip>
            <Button
              variant="ghost"
              size="sm"
              onClick={openSectionSettings}
            >
              <Settings className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeSection(section.id)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>

        {section.subsections.length > 0 && (
          <div className="mb-6 flex gap-2">
            {section.subsections.map((subsectionItem, subsectionIndex) => (
              <div
                key={subsectionItem.id}
                className="flex-1 cursor-pointer"
                onClick={() => navigateTo(currentSectionIndex, subsectionIndex)}
              >
                <div
                  className={cn(
                    'h-2 rounded-full transition-colors',
                    subsectionIndex === currentSubsectionIndex ? 'bg-primary' : 'bg-primary-200',
                  )}
                />
                <p
                  className={cn(
                    'mt-1 truncate text-xs',
                    subsectionIndex === currentSubsectionIndex
                      ? 'text-primary font-medium'
                      : 'text-gray-500',
                  )}
                >
                  {subsectionItem.title || 'Untitled'}
                </p>
              </div>
            ))}
          </div>
        )}

        {!subsection ? (
          <Card>
            <div className="p-8 text-center">
              <p className="mb-4 text-gray-500">This section has no subsections.</p>
              <Button onClick={() => addSubsection(section.id, createNewSubsection())}>
                <Plus className="mr-2 size-4" />
                Add Subsection
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-0">
            <div className="p-6">
              <div className="mb-6 flex items-center gap-2">
                <div className="bg-primary h-2 w-2 rounded-full" />
                <h2 className="flex-1 text-lg font-semibold text-gray-950">
                  {subsection.title || 'Untitled Subsection'}
                </h2>
                {subsection.conditional && (
                  <Tooltip>
                    <TooltipTrigger className="cursor-default">
                      <Badge variant="success">Conditions</Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs p-2">
                      {formatConditionalBehaviorText(
                        subsection.conditional,
                        formDefinition.questions,
                      )}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        duplicateSubsection(currentSectionIndex, currentSubsectionIndex)
                      }
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Duplicate Subsection</TooltipContent>
                </Tooltip>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openSubsectionSettings}
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSubsection(section.id, subsection.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="space-y-6">
                {subsection.questionGroups.map((group, groupIndex) => (
                  <BuilderQuestionGroup
                    key={group.id}
                    section={section}
                    subsection={subsection}
                    group={group}
                    index={groupIndex}
                  />
                ))}
              </div>

              <div className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => addGroup(section.id, subsection.id, createNewGroup())}
                >
                  <Plus className="mr-2 size-4" />
                  Add Question Group
                </Button>
              </div>

              <div className="mt-8 flex items-center justify-end gap-3 border-t pt-4">
                <Button
                  variant="outline"
                  disabled={!canGoPrevious}
                  onClick={navigatePrevious}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  Previous
                </Button>
                <Button
                  disabled={!canGoNext}
                  onClick={navigateNext}
                >
                  Next
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {showSectionSettings && (
        <SectionSettingsDialog
          open={showSectionSettings}
          section={section}
          questions={formDefinition.questions}
          onSave={updates => updateSection(section.id, updates)}
          onClose={closeSectionSettings}
        />
      )}

      {showSubsectionSettings && subsection && (
        <SubsectionSettingsDialog
          open={showSubsectionSettings}
          subsection={subsection}
          questions={formDefinition.questions}
          onSave={updates => updateSubsection(section.id, subsection.id, updates)}
          onClose={closeSubsectionSettings}
        />
      )}
    </div>
  );
}
